import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClientIntegrationsAPI } from '../services/clientIntegrations';
import { getErrorMessage, isUnauthorizedError } from '../services/http';

const TABS = [
  { id: 'suggestions', label: 'Sugestões' },
  { id: 'bling', label: 'Bling sem vínculo' },
  { id: 'auvo', label: 'Auvo sem vínculo' },
];

function customerName(row = {}) {
  return row.nome || row.nomeFantasia || row.id || '-';
}

function customerLine(row = {}) {
  return [row.documento, row.email, row.telefone].filter(Boolean).join(' | ') || 'Sem documento, e-mail ou telefone';
}

function Snapshot({ title, data = {}, id }) {
  return (
    <div className="border rounded p-2 h-100">
      <div className="d-flex justify-content-between gap-2">
        <strong>{title}</strong>
        <span className="text-muted small">{id}</span>
      </div>
      <div>{customerName(data)}</div>
      <small className="text-muted">{customerLine(data)}</small>
    </div>
  );
}

export default function ClientIntegrationConciliation({ onUnauthorized }) {
  const [activeTab, setActiveTab] = useState('suggestions');
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [blingRows, setBlingRows] = useState([]);
  const [auvoRows, setAuvoRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [manualLink, setManualLink] = useState({ blingContatoId: '', auvoClienteId: '' });
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const query = useMemo(() => ({ search, limit: 100 }), [search]);

  const handleError = useCallback((requestError, fallback) => {
    if (isUnauthorizedError(requestError)) {
      onUnauthorized?.();
      return;
    }
    setError(getErrorMessage(requestError, fallback));
  }, [onUnauthorized]);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await ClientIntegrationsAPI.suggestions({ ...query, status: 'pending' });
      setSuggestions(Array.isArray(payload.data) ? payload.data : []);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível carregar as sugestões.');
    } finally {
      setLoading(false);
    }
  }, [handleError, query]);

  const loadUnlinked = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [bling, auvo] = await Promise.all([
        ClientIntegrationsAPI.unlinkedBling(query),
        ClientIntegrationsAPI.unlinkedAuvo(query),
      ]);
      setBlingRows(Array.isArray(bling.data) ? bling.data : []);
      setAuvoRows(Array.isArray(auvo.data) ? auvo.data : []);
    } catch (requestError) {
      handleError(requestError, 'Não foi possível carregar clientes sem vínculo.');
    } finally {
      setLoading(false);
    }
  }, [handleError, query]);

  useEffect(() => {
    if (activeTab === 'suggestions') {
      loadSuggestions();
      return;
    }
    loadUnlinked();
  }, [activeTab, loadSuggestions, loadUnlinked]);

  const generateSuggestions = async () => {
    setActionId('generate');
    setError('');
    setNotice('');
    try {
      const payload = await ClientIntegrationsAPI.generateSuggestions();
      const result = payload.result || payload;
      setNotice(`Sugestões atualizadas: ${result.created || 0} novas, ${result.updated || 0} revisadas.`);
      await loadSuggestions();
      setActiveTab('suggestions');
    } catch (requestError) {
      handleError(requestError, 'Não foi possível gerar sugestões.');
    } finally {
      setActionId('');
    }
  };

  const confirmSuggestion = async (suggestion) => {
    setActionId(`confirm:${suggestion._id}`);
    setError('');
    setNotice('');
    try {
      await ClientIntegrationsAPI.confirmSuggestion(suggestion._id);
      setNotice('Vínculo confirmado.');
      await loadSuggestions();
    } catch (requestError) {
      handleError(requestError, 'Não foi possível confirmar o vínculo.');
    } finally {
      setActionId('');
    }
  };

  const submitReject = async () => {
    if (!rejecting) return;
    setActionId(`reject:${rejecting._id}`);
    setError('');
    setNotice('');
    try {
      await ClientIntegrationsAPI.rejectSuggestion(rejecting._id, { reason: rejectReason });
      setNotice('Sugestão rejeitada.');
      setRejecting(null);
      setRejectReason('');
      await loadSuggestions();
    } catch (requestError) {
      handleError(requestError, 'Não foi possível rejeitar a sugestão.');
    } finally {
      setActionId('');
    }
  };

  const submitManualLink = async (event) => {
    event.preventDefault();
    setActionId('manual-link');
    setError('');
    setNotice('');
    try {
      await ClientIntegrationsAPI.manualLink(manualLink);
      setNotice('Vínculo manual criado.');
      setManualLink({ blingContatoId: '', auvoClienteId: '' });
      await loadUnlinked();
      await loadSuggestions();
    } catch (requestError) {
      handleError(requestError, 'Não foi possível criar o vínculo manual.');
    } finally {
      setActionId('');
    }
  };

  const renderSuggestions = () => (
    <div className="d-grid gap-3">
      {suggestions.map((suggestion) => (
        <div className="card" key={suggestion._id}>
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between gap-3">
              <div>
                <strong>{suggestion.score}% de compatibilidade</strong>
                <div className="text-muted small">{(suggestion.reasons || []).join(', ') || 'Sem motivo informado'}</div>
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-danger" disabled={Boolean(actionId)} onClick={() => setRejecting(suggestion)}>Rejeitar</button>
                <button type="button" className="btn btn-sm btn-success" disabled={Boolean(actionId)} onClick={() => confirmSuggestion(suggestion)}>
                  {actionId === `confirm:${suggestion._id}` ? 'Confirmando...' : 'Confirmar'}
                </button>
              </div>
            </div>
            <div className="row g-3 mt-1">
              <div className="col-md-6"><Snapshot title="Bling" data={suggestion.blingSnapshot} id={suggestion.blingContatoId} /></div>
              <div className="col-md-6"><Snapshot title="Auvo" data={suggestion.auvoSnapshot} id={suggestion.auvoClienteId} /></div>
            </div>
          </div>
        </div>
      ))}
      {!loading && suggestions.length === 0 && <div className="empty-state">Nenhuma sugestão pendente.</div>}
    </div>
  );

  const renderRows = (rows, source) => (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th>ID</th>
            <th>Cliente</th>
            <th>Documento</th>
            <th>Contato</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td className="text-muted">{row.id}</td>
              <td>{customerName(row)}</td>
              <td>{row.documento || '-'}</td>
              <td>{[row.email, row.telefone].filter(Boolean).join(' | ') || '-'}</td>
              <td className="text-end">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setManualLink((current) => ({ ...current, [source === 'bling' ? 'blingContatoId' : 'auvoClienteId']: row.id }))}
                >
                  Usar no vínculo
                </button>
              </td>
            </tr>
          ))}
          {!loading && rows.length === 0 && (
            <tr><td colSpan="5" className="text-center text-muted py-4">Nenhum cliente encontrado.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <main className="client-consolidation-page">
      <section className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <div className="eyebrow">Clientes</div>
          <h2>Conciliação Bling/Auvo</h2>
          <p className="text-muted mb-0">Confirme manualmente quais cadastros representam o mesmo cliente.</p>
        </div>
        <button type="button" className="btn btn-primary" disabled={Boolean(actionId)} onClick={generateSuggestions}>
          {actionId === 'generate' ? 'Gerando...' : 'Gerar sugestões'}
        </button>
      </section>

      <section className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <div className="btn-group" role="tablist" aria-label="Visões de conciliação">
          {TABS.map((tab) => (
            <button key={tab.id} type="button" className={`btn btn-sm ${activeTab === tab.id ? 'btn-dark' : 'btn-outline-dark'}`} onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>
        <input className="form-control form-control-sm" style={{ maxWidth: 320 }} value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome, documento, e-mail ou ID" />
      </section>

      {notice && <div className="alert alert-success py-2">{notice}</div>}
      {error && <div className="alert alert-danger py-2">{error}</div>}

      <form className="row g-2 align-items-end mb-3" onSubmit={submitManualLink}>
        <div className="col-md-4">
          <label className="form-label">ID contato Bling</label>
          <input className="form-control" value={manualLink.blingContatoId} onChange={(event) => setManualLink((current) => ({ ...current, blingContatoId: event.target.value }))} />
        </div>
        <div className="col-md-4">
          <label className="form-label">ID cliente Auvo</label>
          <input className="form-control" value={manualLink.auvoClienteId} onChange={(event) => setManualLink((current) => ({ ...current, auvoClienteId: event.target.value }))} />
        </div>
        <div className="col-md-4">
          <button type="submit" className="btn btn-outline-primary w-100" disabled={actionId === 'manual-link' || !manualLink.blingContatoId || !manualLink.auvoClienteId}>
            {actionId === 'manual-link' ? 'Vinculando...' : 'Criar vínculo manual'}
          </button>
        </div>
      </form>

      {loading && <div className="text-muted py-3">Carregando...</div>}
      {!loading && activeTab === 'suggestions' && renderSuggestions()}
      {!loading && activeTab === 'bling' && renderRows(blingRows, 'bling')}
      {!loading && activeTab === 'auvo' && renderRows(auvoRows, 'auvo')}

      {rejecting && (
        <div className="modal-backdrop-lite" role="dialog" aria-modal="true">
          <div className="task-modal">
            <h3>Rejeitar sugestão</h3>
            <p className="text-muted">A sugestão rejeitada não volta como pendente nas próximas gerações.</p>
            <textarea className="form-control" rows="3" value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} placeholder="Motivo opcional" />
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setRejecting(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" disabled={Boolean(actionId)} onClick={submitReject}>
                {actionId === `reject:${rejecting._id}` ? 'Rejeitando...' : 'Rejeitar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
